import { type CountryCode, isValidPhoneNumber } from "libphonenumber-js";
import { useEffect, useMemo } from "react";
import type { UseFormReturn } from "react-hook-form";
import { z } from "zod";

import { type SelectOption } from "@/shared/components/forms/ColocaSelectField";
import type { DocumentTypeCode } from "@/shared/data/document-types";

import { documentTypesFor, isDocumentNumberLengthValid } from "../../constants/clientDocumentTypes.constants";
import type { BusinessIdentityRequest } from "../../types/clients-api.types";
import { MAX_EMAIL } from "../../utils/emailValidation";
import { normalizePhone, PERSON_NAME_REGEX } from "./client-form.utils";

/** Localized messages for the shared business-identity schema (validation errors are per-locale). */
export interface BusinessIdentitySchemaMessages {
  phone: string;
  incorporationDate: string;
  nameFormat: string;
  /** Message for a document type with a fixed length (CNPJ), or `undefined` when the type has none. */
  documentLength: (code: string) => string | undefined;
}

/**
 * Shared schema for the nested `BusinessIdentityDto` (coloca-nest qa). Backs both the business subclient
 * wizard and the root organization wizard — the two flows capture the same company + legal-owner fields and
 * only differ in step layout and the final request body. `legalNameMinLength` is the sole field-level delta
 * (the org row requires >= 2 chars for its derived `name`; a subclient allows >= 1).
 */
export const buildBusinessIdentitySchema = (
  messages: BusinessIdentitySchemaMessages,
  { legalNameMinLength = 1 }: { legalNameMinLength?: number } = {},
) =>
  z
    .object({
      legal_name: z.string().min(legalNameMinLength).max(255),
      business_type: z.string().min(1).max(100),
      country: z.string().length(3),
      identification_type: z.string().min(1),
      // Mirrors the backend `@Matches` on BusinessIdentityDto.identification_number. The slash is here for
      // Brazil's CNPJ (12.345.678/0001-95); the backend strips it, along with dots and dashes, before the
      // number becomes part of the dedupe key.
      identification_number: z
        .string()
        .min(1)
        .max(100)
        .regex(/^[A-Za-z0-9.\-/ ]+$/),
      incorporation_date: z
        .string()
        .min(1)
        .regex(/^\d{4}-\d{2}-\d{2}$/, messages.incorporationDate),
      email: z.string().email().max(MAX_EMAIL),
      phone: z.string().min(1),
      phone_country: z.string().length(2),
      owner_name: z.string().min(1).max(255).regex(PERSON_NAME_REGEX, messages.nameFormat),
      owner_country_code: z.string().length(3),
      owner_id_type: z.string().min(1),
      owner_id_number: z.string().min(1).max(255),
      owner_address_line1: z.string().min(1).max(500),
      owner_city: z.string().min(1).max(255),
      owner_state: z.string().min(1).max(255),
      owner_postal_code: z.string().min(1).max(50),
    })
    .superRefine((data, ctx) => {
      if (!isValidPhoneNumber(data.phone, data.phone_country as CountryCode)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["phone"], message: messages.phone });
      }
      // The backend validates the character set but no length, so a mistyped CNPJ is only caught here.
      if (!isDocumentNumberLengthValid(data.identification_type, data.identification_number)) {
        const message = messages.documentLength(data.identification_type);
        if (message) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["identification_number"],
            message,
          });
        }
      }
      if (!isDocumentNumberLengthValid(data.owner_id_type, data.owner_id_number)) {
        const message = messages.documentLength(data.owner_id_type);
        if (message) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["owner_id_number"],
            message,
          });
        }
      }
    });

export type BusinessIdentityValues = z.infer<ReturnType<typeof buildBusinessIdentitySchema>>;

export const BUSINESS_IDENTITY_DEFAULTS: BusinessIdentityValues = {
  legal_name: "",
  business_type: "",
  country: "COL",
  identification_type: "NIT",
  identification_number: "",
  incorporation_date: "",
  email: "",
  phone: "",
  phone_country: "",
  owner_name: "",
  owner_country_code: "COL",
  owner_id_type: "CC",
  owner_id_number: "",
  owner_address_line1: "",
  owner_city: "",
  owner_state: "",
  owner_postal_code: "",
};

/** Maps validated form values to the `BusinessIdentityRequest` body shared by both flows. */
export const buildBusinessIdentityRequest = (values: BusinessIdentityValues): BusinessIdentityRequest => ({
  legal_name: values.legal_name.trim(),
  business_type: values.business_type.trim(),
  country: values.country,
  identification_number: values.identification_number.trim(),
  identification_type: values.identification_type,
  incorporation_date: values.incorporation_date,
  email: values.email.trim(),
  phone: normalizePhone(values.phone),
  owner_name: values.owner_name.trim(),
  owner_id_type: values.owner_id_type,
  owner_id_number: values.owner_id_number.trim(),
  owner_address_line1: values.owner_address_line1.trim(),
  owner_city: values.owner_city.trim(),
  owner_state: values.owner_state.trim(),
  owner_postal_code: values.owner_postal_code.trim(),
  owner_country_code: values.owner_country_code,
});

/**
 * Keeps the country-dependent document-type selectors in sync (backend `@IsValidDocumentType`). Company doc
 * uses category "fiscal" (the company tax ID / Fiscal ID) keyed on `country`; owner doc uses category
 * "personal" keyed on `owner_country_code`. When the country changes and the current selection is no longer
 * valid, it resets to the first available type. Returns the `SelectOption` items for both selectors;
 * `labelFor` resolves each code to its localized label.
 */
export function useDocumentTypeSync(
  form: UseFormReturn<BusinessIdentityValues, unknown, BusinessIdentityValues>,
  labelFor: (code: DocumentTypeCode) => string,
): { companyIdTypeItems: SelectOption[]; ownerIdTypeItems: SelectOption[] } {
  const country = form.watch("country");
  const ownerCountry = form.watch("owner_country_code");
  const companyIdTypeCodes = useMemo(() => documentTypesFor(country, "fiscal"), [country]);
  const ownerIdTypeCodes = useMemo(() => documentTypesFor(ownerCountry, "personal"), [ownerCountry]);
  const companyIdTypeItems = useMemo(
    () => companyIdTypeCodes.map((code) => ({ value: code, label: labelFor(code) })),
    [companyIdTypeCodes, labelFor],
  );
  const ownerIdTypeItems = useMemo(
    () => ownerIdTypeCodes.map((code) => ({ value: code, label: labelFor(code) })),
    [ownerIdTypeCodes, labelFor],
  );

  // When a type is actually replaced the number typed for the old one goes with it: a NIT left behind under a
  // CNPJ passes every check and is stored as the wrong document. A type that survives the country change
  // keeps its number, since nothing about it became wrong.
  useEffect(() => {
    const current = form.getValues("identification_type");
    if (companyIdTypeCodes.length > 0 && !(companyIdTypeCodes as readonly string[]).includes(current)) {
      form.setValue("identification_type", companyIdTypeCodes[0], { shouldValidate: false });
      form.setValue("identification_number", "", { shouldValidate: false });
      // The error belonged to the document that just went away. `shouldValidate: false` keeps the emptied
      // field from turning red on its own, but it also leaves the old message pinned under it — a CNPJ
      // length complaint sitting below a blank field that now expects a NIT.
      form.clearErrors("identification_number");
    }
  }, [companyIdTypeCodes, form]);

  useEffect(() => {
    const current = form.getValues("owner_id_type");
    if (ownerIdTypeCodes.length > 0 && !(ownerIdTypeCodes as readonly string[]).includes(current)) {
      form.setValue("owner_id_type", ownerIdTypeCodes[0], { shouldValidate: false });
      form.setValue("owner_id_number", "", { shouldValidate: false });
      form.clearErrors("owner_id_number");
    }
  }, [ownerIdTypeCodes, form]);

  return { companyIdTypeItems, ownerIdTypeItems };
}
